import React, { createContext, useCallback, useContext } from 'react';
import { useQuery } from 'react-query';
import { useSetRecoilState } from 'recoil';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../services/api';
import FeatureGateError, { DEFAULT_FEATURE_GATE_ERROR_MESSAGE } from '../errors/FeatureGateError';
import useAccount from '../hooks/account/useAccount';
import { premiumModal } from '../atoms/shared';

const FeatureGateContext = createContext();

const FeatureGateProvider = ({ children }) => {
  const setPremiumModalPayload = useSetRecoilState(premiumModal);
  const { accountId } = useAccount();
  const { isAccountNetworkAccessSupplier } = useContext(ConfigContext);

  const enabled = isAccountNetworkAccessSupplier;
  const storeKey = ['features-gate', accountId];
  const { data } = useQuery(storeKey, () => api.fetchFeatureGate({ accountId }), {
    staleTime: 0,
    enabled,
    refetchInterval: 1000 * 15 // 15 seconds
  });
  const limits = data?.data?.limits;
  const usage = data?.data?.usage;

  const checkFeatureGate = useCallback(
    (featureName, callback) => (...params) => {
      if (enabled && limits?.[featureName] !== undefined) {
        const leftToUse = limits[featureName] - (usage?.[featureName] || 0);
        if (leftToUse <= 0) {
          setPremiumModalPayload({ featureType: featureName, source: featureName });

          throw new FeatureGateError(DEFAULT_FEATURE_GATE_ERROR_MESSAGE);
        }
      }
      return callback(...params);
    },
    [enabled, limits, setPremiumModalPayload, usage]
  );

  const getFeatureGateUsage = (featureName) => usage?.[featureName];

  const value = { checkFeatureGate, getFeatureGateUsage };

  return <FeatureGateContext.Provider value={value}>{children}</FeatureGateContext.Provider>;
};

export const useFeatureGate = () => {
  const context = useContext(FeatureGateContext);

  if (!context) {
    throw new Error('useFeatureGate must be used within a FeatureGateProvider');
  }

  return context;
};

export { FeatureGateContext, FeatureGateProvider };
